/**
 * DbProductTitle - 电商商品标题组件
 *
 * 特性：
 * - 支持单行/多行省略号
 * - 支持前置/后置标签
 * - 支持标签与文字在同一行（内联标签）
 * - 支持关键词高亮
 * - 支持自定义样式
 */

import React, { useMemo } from 'react';
import { View, Text, TouchableOpacity, TextStyle, ViewStyle } from 'react-native';
import {
    DbProductTitleProps,
    DbProductTitleInlineProps,
    TitleTagConfig,
} from './DbProductTitle.types';
import { createStyles, DEFAULT_TAG_STYLE } from './DbProductTitle.styles';

// ==================== 内部标签组件 ====================

interface InternalTagProps {
    config: TitleTagConfig;
    style?: ViewStyle;
}

const InternalTag: React.FC<InternalTagProps> = ({ config, style }) => {
    const tagStyle: ViewStyle = {
        paddingHorizontal: DEFAULT_TAG_STYLE.paddingHorizontal,
        paddingVertical: DEFAULT_TAG_STYLE.paddingVertical,
        borderRadius: DEFAULT_TAG_STYLE.borderRadius,
        borderWidth: config.outline ? 1 : 0,
        borderColor: config.borderColor || config.color || '#FF2442',
        backgroundColor: config.outline ? 'transparent' : (config.backgroundColor || config.color || '#FF2442'),
        ...((style as object) || {}),
        ...((config.style as object) || {}),
    };

    const textStyle: TextStyle = {
        fontSize: DEFAULT_TAG_STYLE.fontSize,
        fontWeight: '500',
        color: config.outline 
            ? (config.color || '#FF2442') 
            : '#FFFFFF',
        ...((config.textStyle as object) || {}),
    };

    return (
        <View style={tagStyle}>
            <Text style={textStyle}>{config.text}</Text>
        </View>
    );
};

// ==================== DbProductTitle 组件 ====================

export const DbProductTitle: React.FC<DbProductTitleProps> & {
    Inline: typeof DbProductTitleInline;
} = ({
    title,
    numberOfLines = 2,
    prefixTags = [],
    suffixTags = [],
    tagGap = 4,
    tagSpacing = 4,
    fontSize = 14,
    color = '#333333',
    fontWeight = '500',
    lineHeight,
    style,
    titleStyle,
    tagContainerStyle,
    highlightKeywords = [],
    highlightColor = '#FF2442',
    onPress,
    onLongPress,
}) => {
    const styles = useMemo(
        () => createStyles(fontSize, lineHeight),
        [fontSize, lineHeight]
    );

    // 渲染高亮文本
    const renderHighlightedText = (text: string) => {
        if (highlightKeywords.length === 0) {
            return text;
        }

        // 创建正则匹配所有关键词
        const regex = new RegExp(`(${highlightKeywords.join('|')})`, 'gi');
        const parts = text.split(regex);

        return parts.map((part, index) => {
            const isHighlight = highlightKeywords.some(
                keyword => keyword.toLowerCase() === part.toLowerCase()
            );
            if (isHighlight) {
                return (
                    <Text key={index} style={{ color: highlightColor }}>
                        {part}
                    </Text>
                );
            }
            return part;
        });
    };

    // 渲染标签列表
    const renderTags = (tags: TitleTagConfig[], isPrefix: boolean) => {
        if (tags.length === 0) return null;

        return (
            <View
                style={[
                    isPrefix ? styles.prefixTagContainer : styles.suffixTagContainer,
                    { gap: tagSpacing },
                    isPrefix ? { marginRight: tagGap } : { marginLeft: tagGap },
                    tagContainerStyle,
                ]}
            >
                {tags.map((tag, index) => (
                    <InternalTag key={index} config={tag} />
                ))}
            </View>
        );
    };

    const content = (
        <View style={[styles.container, style]}>
            {renderTags(prefixTags, true)}
            <Text
                style={[
                    styles.title,
                    { color, fontWeight },
                    titleStyle,
                ]}
                numberOfLines={numberOfLines}
            >
                {renderHighlightedText(title)}
            </Text>
            {renderTags(suffixTags, false)}
        </View>
    );

    if (onPress || onLongPress) {
        return (
            <TouchableOpacity
                onPress={onPress}
                onLongPress={onLongPress}
                activeOpacity={0.7}
            >
                {content}
            </TouchableOpacity>
        );
    }

    return content;
};

// ==================== DbProductTitleInline 组件（内联标签版本） ====================

export const DbProductTitleInline: React.FC<DbProductTitleInlineProps> = ({
    title,
    numberOfLines = 2,
    inlineTags = [],
    tagPosition = 'start',
    tagGap = 4,
    tagSpacing = 4,
    fontSize = 14,
    color = '#333333',
    fontWeight = '500',
    lineHeight,
    style,
    titleStyle,
    onPress,
}) => {
    const computedLineHeight = lineHeight || fontSize * 1.5;

    // 渲染内联标签（使用 View 实现，样式与 DbTag 一致）
    const renderInlineTags = () => {
        return inlineTags.map((tag, index) => {
            const isOutline = tag.outline === true;
            const baseColor = tag.color || '#FF2442';
            
            // 背景色
            const tagBgColor = isOutline
                ? 'transparent'
                : (tag.backgroundColor || baseColor);
            
            // 文字色
            const tagTextColor = isOutline
                ? baseColor
                : (tag.textColor || '#FFFFFF');
            
            // 边框色
            const tagBorderColor = tag.borderColor || baseColor;

            // 计算间距：最后一个标签用 tagGap，其他用 tagSpacing
            const marginRight = index < inlineTags.length - 1 ? tagSpacing : tagGap;

            return (
                <View
                    key={index}
                    style={{
                        backgroundColor: tagBgColor,
                        borderWidth: isOutline ? 1 : 0,
                        borderColor: tagBorderColor,
                        borderRadius: 2,
                        paddingHorizontal: 4,
                        paddingVertical: 2,
                        marginRight,
                        justifyContent: 'center',
                        alignItems: 'center',
                    }}
                >
                    <Text
                        style={{
                            fontSize: 10,
                            fontWeight: '500',
                            color: tagTextColor,
                            includeFontPadding: false,
                            textAlignVertical: 'center',
                        }}
                    >
                        {tag.text}
                    </Text>
                </View>
            );
        });
    };

    // 将标题拆分成单个字符，每个字符作为独立的 Text 元素
    // 这样可以实现第二行从最左边开始的效果
    const renderTitleCharacters = () => {
        return title.split('').map((char, index) => (
            <Text
                key={`char-${index}`}
                style={[
                    {
                        fontSize,
                        lineHeight: computedLineHeight,
                        color,
                        fontWeight,
                        includeFontPadding: false,
                    },
                    titleStyle,
                ]}
            >
                {char}
            </Text>
        ));
    };

    const content = (
        <View
            style={[
                {
                    flexDirection: 'row',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                },
                style,
            ]}
        >
            {tagPosition === 'start' && renderInlineTags()}
            {renderTitleCharacters()}
            {tagPosition === 'end' && (
                <>
                    <View style={{ width: tagGap }} />
                    {renderInlineTags()}
                </>
            )}
        </View>
    );

    // 如果需要限制行数，使用裁剪容器包裹
    const wrappedContent = numberOfLines ? (
        <View
            style={{
                maxHeight: computedLineHeight * numberOfLines,
                overflow: 'hidden',
            }}
        >
            {content}
        </View>
    ) : content;

    if (onPress) {
        return (
            <TouchableOpacity onPress={onPress} activeOpacity={0.7}>
                {wrappedContent}
            </TouchableOpacity>
        );
    }

    return wrappedContent;
};

// 挂载子组件
DbProductTitle.Inline = DbProductTitleInline;
DbProductTitle.displayName = 'DbProductTitle';

export default DbProductTitle;
export type {
    DbProductTitleProps,
    DbProductTitleInlineProps,
    TitleTagConfig,
    TagPosition,
} from './DbProductTitle.types';
